import { create } from '@bufbuild/protobuf'
import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { type BusinessPurpose } from '@ebalance/proto/bfftypes/v1/business_purpose_pb'
import { type LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { type BusinessMasterListItem as ProtoBusinessMasterListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { listBusinessMasters } from '@ebalance/proto/bffuser/v1/occto_master_service-OcctoMasterService_connectquery'
import {
  ListBusinessMastersRequestSchema,
  type ListBusinessMastersResponse,
} from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import {
  type CivilDate,
  protoDateToCivilDate,
  requireCivilDate,
} from '~/models/civilDate'

// Frontend display row type. Flattens optional proto fields into
// `undefined`-bearing primitives. `updatedAt` keeps the raw proto Timestamp
// because JS `Date` only carries ms precision; converting on receipt and
// re-converting on send loses the µs portion stored in PostgreSQL and
// breaks optimistic-lock `expected_updated_at` comparison (= delete /
// update returns Aborted). Display code that wants a `Date` should call
// `timestampDate` at the call site. `validFrom` / `validTo` are kept as
// `CivilDate` ({year, month, day}, day-precision, no `Date` conversion) so
// they compose directly with the shared `useExpiryFilter` / `expiryStatus`
// (occto-master-valid-period-6tabs 設計書 §5.4).
export type BusinessMasterRow = {
  id: string
  businessCode: string
  businessName: string
  licenseTypes: LicenseType[]
  purpose: BusinessPurpose
  updatedBy: string | undefined
  updatedAt: Timestamp | undefined
  /** 適用開始日 (必須)。欠落は契約違反として converter が throw する。 */
  validFrom: CivilDate
  validTo: CivilDate | undefined
  /** 登録小売電気事業者 登録番号 (任意、AC-OMU-013)。未入力は undefined。 */
  registrationNumber: string | undefined
}

function convertItem(item: ProtoBusinessMasterListItem): BusinessMasterRow {
  return {
    id: item.id,
    businessCode: item.businessCode,
    businessName: item.businessName,
    licenseTypes: item.licenseTypes,
    purpose: item.purpose,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `BusinessMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
    registrationNumber: item.registrationNumber,
  }
}

export function convertListBusinessMastersResponse(
  resp: ListBusinessMastersResponse,
): BusinessMasterRow[] {
  return resp.items.map(convertItem)
}

/**
 * BusinessMasterListItem[] (= 集約 RPC レスポンスの field 型) を BusinessMasterRow[] に変換する。
 * 集約 RPC (GetDemandSuppressionPlanMasterFormOptions) は BusinessMasterListItem[] を
 * 直接フィールドとして返すため、convertListBusinessMastersResponse の代わりに使う。
 */
export function convertBusinessMasterListItems(
  items: ProtoBusinessMasterListItem[],
): BusinessMasterRow[] {
  return items.map(convertItem)
}

/**
 * @param options.throwOnError 既定の `ApiClientProvider` は `throwOnError: true`
 *   (= error boundary = 全画面エラーに飛ばす)。 事業者マスタを選択肢の供給源としてしか
 *   使わない画面 (= 取得できなければ選択肢が空になるだけで、 画面本体は描画したい) は
 *   `false` を渡す。
 */
export function useListBusinessMasters(options?: {
  throwOnError?: boolean
}): UseQueryResult<ListBusinessMastersResponse, ConnectError> {
  return useQuery(
    listBusinessMasters,
    create(ListBusinessMastersRequestSchema, {}),
    // throwOnError は未指定なら key 自体を渡さず QueryClient 既定 (= true) に従う
    // (= `throwOnError: undefined` を渡すと既定 true を上書きしてしまうため)。
    options?.throwOnError !== undefined
      ? { throwOnError: options.throwOnError }
      : {},
  )
}
